import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, CheckCircle2, Circle, PlayCircle, FileText, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PublicShell, Loading } from "@/components/shared";
import { TIERS } from "@/lib/constants";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["course", slug], queryFn: async () => (await api.get(`/courses/${slug}`)).data });
  const isSales = data?.access_type === "purchase";
  const { data: sm } = useQuery({ queryKey: ["sm-templates", slug], queryFn: async () => (await api.get("/sales-mastery/info")).data, enabled: !!isSales && data && !data.locked });
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (data && !data.locked && !active) {
      const first = data.modules?.[0]?.lessons?.[0];
      if (first) setActive(first);
    }
  }, [data, active]);

  if (isLoading) return <PublicShell><Loading /></PublicShell>;
  if (!data) return <PublicShell><div className="py-24 text-center text-slate-500">Course not found.</div></PublicShell>;

  const complete = async (lesson) => {
    if (!user) return navigate("/login");
    try { const res = await api.post(`/lessons/${lesson.id}/complete`); toast.success(res.data.completed ? "Marked complete" : "Marked incomplete"); refetch(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upgrade required"); }
  };
  const pct = data.total ? Math.round((data.done / data.total) * 100) : 0;

  if (data.locked) {
    const isPurchase = data.access_type === "purchase";
    return (
      <PublicShell>
        <section className="bg-navy grain"><div className="relative mx-auto max-w-5xl px-5 py-16 lg:px-8">
          <button onClick={() => navigate("/courses")} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to courses</button>
          <h1 className="font-heading text-4xl font-bold text-white">{data.title}</h1>
          <p className="mt-3 max-w-2xl text-slate-400">{data.subtitle}</p>
        </div></section>
        <section className="mx-auto max-w-3xl px-5 py-16 text-center lg:px-8">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-lg bg-emerald/10 text-emerald"><Lock className="h-6 w-6" /></div>
          {isPurchase ? (
            <>
              <h2 className="mt-5 font-heading text-2xl font-bold text-navy">Get lifetime access to Ultimate Sales Mastery</h2>
              <p className="mx-auto mt-3 max-w-md text-slate-600">Every module, every template, and all future updates — one payment, lifetime access, 14-day refund.</p>
              <Button onClick={() => navigate("/sales-mastery")} className="mt-6 rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="course-buy-btn">View the course</Button>
            </>
          ) : (
            <>
              <h2 className="mt-5 font-heading text-2xl font-bold text-navy">This course requires {TIERS[data.membership_requirement]?.label}</h2>
              <p className="mx-auto mt-3 max-w-md text-slate-600">Get full access to the course academy, premium community and the complete resource vault.</p>
              <Button onClick={() => navigate("/pricing")} className="mt-6 rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="course-upgrade-btn">See plans</Button>
            </>
          )}
        </section>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <section className="bg-navy grain"><div className="relative mx-auto max-w-7xl px-5 py-12 lg:px-8">
        <button onClick={() => navigate("/courses")} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white" data-testid="course-back"><ArrowLeft className="h-4 w-4" /> Back to courses</button>
        <h1 className="font-heading text-3xl font-bold text-white sm:text-4xl">{data.title}</h1>
        <p className="mt-2 max-w-2xl text-slate-400">{data.subtitle}</p>
        <div className="mt-5 max-w-md"><Progress value={pct} className="h-2" /><p className="mt-2 text-sm text-slate-400">{data.done}/{data.total} lessons · {pct}% complete</p></div>
      </div></section>

      <section className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {active ? (
              <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="lesson-viewer">
                {active.type === "video" ? (
                  <div className="mb-5 flex aspect-video items-center justify-center rounded-lg bg-navy text-white"><PlayCircle className="h-14 w-14 opacity-80" /></div>
                ) : (
                  <div className="mb-5 flex items-center gap-2 text-emerald"><FileText className="h-5 w-5" /> <span className="font-semibold">Text lesson</span></div>
                )}
                <h2 className="font-heading text-2xl font-bold text-navy">{active.title}</h2>
                <p className="mt-3 leading-relaxed text-slate-700">{active.content}</p>
                <Button onClick={() => complete(active)} className={`mt-6 rounded-md ${active.completed ? "bg-slate-200 text-navy hover:bg-slate-300" : "bg-emerald text-white hover:bg-emerald-700"}`} data-testid="lesson-complete-btn">
                  {active.completed ? <><CheckCircle2 className="mr-2 h-4 w-4" /> Completed</> : "Mark as complete"}
                </Button>
              </div>
            ) : <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">Select a lesson to begin.</div>}
            {isSales && sm?.templates?.length > 0 && (
              <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="course-templates">
                <h3 className="font-heading text-lg font-bold text-navy">Your templates</h3>
                <p className="mt-1 text-sm text-slate-500">Download the working files for each module. New templates appear here as modules are released.</p>
                <div className="mt-4 divide-y divide-slate-100">
                  {sm.templates.map((t) => (
                    <div key={t.id} className="flex items-center justify-between gap-3 py-3" data-testid={`course-template-${t.id}`}>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-navy">{t.name} <span className="ml-1 text-[11px] font-medium text-slate-400">{t.file_type}</span></p>
                        <p className="text-xs text-slate-500">Module {t.module}: {t.module_title}</p>
                      </div>
                      {t.file_url ? (
                        <a href={t.file_url} target="_blank" rel="noreferrer" data-testid={`course-template-dl-${t.id}`}
                          className="inline-flex items-center gap-1.5 rounded-md bg-emerald px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"><FileText className="h-3.5 w-3.5" /> Download</a>
                      ) : (
                        <span className="whitespace-nowrap rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-500">{t.available_date ? `Available ${t.available_date}` : "Coming soon"}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="px-2 font-heading font-semibold text-navy">Course content</h3>
              <div className="mt-3 space-y-4">
                {data.modules?.map((m, mi) => (
                  <div key={m.id}>
                    <p className="px-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                      Module {mi + 1}: {m.title}
                      {m.coming_soon && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">Coming soon{m.expected_date ? ` · ${m.expected_date}` : ""}</span>}
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {m.coming_soon && (!m.lessons || m.lessons.length === 0) && (
                        <p className="px-2 py-2 text-xs text-slate-400">Recording in progress — you'll get it free when it lands.</p>
                      )}
                      {m.lessons?.map((l) => (
                        <button key={l.id} onClick={() => setActive(l)} data-testid={`lesson-${l.id}`}
                          className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors ${active?.id === l.id ? "bg-emerald/10 text-emerald" : "text-slate-600 hover:bg-slate-50"}`}>
                          {l.completed ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald" /> : <Circle className="h-4 w-4 flex-shrink-0 text-slate-300" />}
                          <span className="flex-1">{l.title}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
